import { FFmpeg } from '@ffmpeg/ffmpeg'
import { fetchFile } from '@ffmpeg/util'

// Create a singleton instance of FFmpeg
const ffmpeg = new FFmpeg({
    log: true,
    corePath: 'https://unpkg.com/@ffmpeg/core@0.12.10/dist/ffmpeg-core.js'
});

// Load FFmpeg if not already loaded
export async function loadFFmpeg() {
    if (!ffmpeg.isLoaded()) {
        try {
            await ffmpeg.load();
        } catch (error) {
            console.error("Failed to load FFmpeg:", error);
            // Add more specific error logging to help with debugging
            if (error.message) {
                console.error("Error message:", error.message);
            }
            if (error.stack) {
                console.error("Error stack:", error.stack);
            }
            throw new Error(`Failed to load FFmpeg: ${error.message || 'Unknown error'}`);
        }
    }
    return ffmpeg;
}

// Trim video function
export async function trimVideo(inputFile, start, end, outputName = 'output.mp4') {
    try {
        await loadFFmpeg();
        ffmpeg.FS('writeFile', 'input.mp4', await fetchFile(inputFile));

        // Use -ss before -i for accurate seeking, and use -t instead of -to for duration
        await ffmpeg.run(
            '-ss', String(start),
            '-i', 'input.mp4',
            '-t', String(end - start),
            '-c:v', 'libx264', // Re-encode for accuracy
            '-c:a', 'aac',     // Re-encode audio
            '-preset', 'ultrafast', // Fast encoding
            '-avoid_negative_ts', '1',
            outputName
        );

        const data = ffmpeg.FS('readFile', outputName);

        // Clean up files
        ffmpeg.FS('unlink', 'input.mp4');
        ffmpeg.FS('unlink', outputName);

        return new Blob([data.buffer], { type: 'video/mp4' });
    } catch (error) {
        console.error("Error trimming video:", error);
        throw error;
    }
}

// Add audio to video (replace or mix)
export async function addAudioToVideo(videoFile, audioFile, replace = true, outputName = 'output.mp4') {
    try {
        await loadFFmpeg();
        ffmpeg.FS('writeFile', 'input.mp4', await fetchFile(videoFile));
        ffmpeg.FS('writeFile', 'audio.mp3', await fetchFile(audioFile));

        if (replace) {
            // Replace original audio
            await ffmpeg.run(
                '-i', 'input.mp4',
                '-i', 'audio.mp3',
                '-map', '0:v',
                '-map', '1:a',
                '-c:v', 'copy',
                '-shortest',
                outputName
            );
        } else {
            // Mix with original audio
            await ffmpeg.run(
                '-i', 'input.mp4',
                '-i', 'audio.mp3',
                '-filter_complex', '[0:a][1:a]amix=inputs=2:duration=longest[a]',
                '-map', '0:v',
                '-map', '[a]',
                '-c:v', 'copy',
                outputName
            );
        }

        const data = ffmpeg.FS('readFile', outputName);

        // Clean up files
        ffmpeg.FS('unlink', 'input.mp4');
        ffmpeg.FS('unlink', 'audio.mp3');
        ffmpeg.FS('unlink', outputName);

        return new Blob([data.buffer], { type: 'video/mp4' });
    } catch (error) {
        console.error("Error adding audio to video:", error);
        throw error;
    }
}

// Add text overlay to video
export async function addTextToVideo(videoFile, text, position = 'center', fontSize = 24, color = 'white', startTime = 0, duration = -1, outputName = 'output.mp4') {
    try {
        await loadFFmpeg();
        ffmpeg.FS('writeFile', 'input.mp4', await fetchFile(videoFile));

        // Map position to x,y coordinates
        let textPositionParams;

        // Check if custom position is provided directly
        if (typeof position === 'string' && position.includes('x=') && position.includes('y=')) {
            // Already formatted as ffmpeg parameters
            textPositionParams = position;
        } else if (position === 'custom' && arguments.length > 8) {
            // Custom position coordinates provided as additional arguments
            const x = arguments[8];
            const y = arguments[9];
            textPositionParams = `x=${x}:y=${y}`;
        } else {
            // Use predefined positions
            switch (position) {
                case 'top':
                    textPositionParams = 'x=(w-text_w)/2:y=h/10';
                    break;
                case 'bottom':
                    textPositionParams = 'x=(w-text_w)/2:y=h-h/8';
                    break;
                case 'left':
                    textPositionParams = 'x=w/10:y=(h-text_h)/2';
                    break;
                case 'right':
                    textPositionParams = 'x=w-w/10-text_w:y=(h-text_h)/2';
                    break;
                case 'center':
                default:
                    textPositionParams = 'x=(w-text_w)/2:y=(h-text_h)/2';
            }
        }

        // Escape special characters in text
        const escapedText = text.replace(/:/g, '\\:').replace(/'/g, "\\'");

        // Create filter with optional timing parameters
        let filter = `drawtext=text='${escapedText}':fontsize=${fontSize}:fontcolor=${color}:${textPositionParams}`;

        // Add timing if needed
        if (duration > 0) {
            filter += `:enable='between(t,${startTime},${startTime + duration})'`;
        }

        await ffmpeg.run(
            '-i', 'input.mp4',
            '-vf', filter,
            '-c:a', 'copy',
            outputName
        );

        const data = ffmpeg.FS('readFile', outputName);

        // Clean up files
        ffmpeg.FS('unlink', 'input.mp4');
        ffmpeg.FS('unlink', outputName);

        return new Blob([data.buffer], { type: 'video/mp4' });
    } catch (error) {
        console.error("Error adding text to video:", error);
        throw error;
    }
}

// Change video speed
export async function changeVideoSpeed(videoFile, speed = 1.0, outputName = 'output.mp4') {
    try {
        await loadFFmpeg();
        ffmpeg.FS('writeFile', 'input.mp4', await fetchFile(videoFile));

        // Speed factor (speed > 1 = faster, speed < 1 = slower)
        const tempo = 1.0 / speed;

        await ffmpeg.run(
            '-i', 'input.mp4',
            '-filter_complex', `[0:v]setpts=${tempo}*PTS[v];[0:a]atempo=${speed}[a]`,
            '-map', '[v]',
            '-map', '[a]',
            outputName
        );

        const data = ffmpeg.FS('readFile', outputName);

        // Clean up files
        ffmpeg.FS('unlink', 'input.mp4');
        ffmpeg.FS('unlink', outputName);

        return new Blob([data.buffer], { type: 'video/mp4' });
    } catch (error) {
        console.error("Error changing video speed:", error);
        throw error;
    }
}

// Extract audio from video
export async function extractAudio(videoFile, outputName = 'output.mp3') {
    try {
        await loadFFmpeg();
        ffmpeg.FS('writeFile', 'input.mp4', await fetchFile(videoFile));

        await ffmpeg.run(
            '-i', 'input.mp4',
            '-vn',  // No video
            '-acodec', 'libmp3lame',
            '-q:a', '2',  // High quality
            outputName
        );

        const data = ffmpeg.FS('readFile', outputName);

        // Clean up files
        ffmpeg.FS('unlink', 'input.mp4');
        ffmpeg.FS('unlink', outputName);

        return new Blob([data.buffer], { type: 'audio/mp3' });
    } catch (error) {
        console.error("Error extracting audio:", error);
        throw error;
    }
}

// Mute video (remove audio)
export async function muteVideo(videoFile, outputName = 'output.mp4') {
    try {
        await loadFFmpeg();
        ffmpeg.FS('writeFile', 'input.mp4', await fetchFile(videoFile));

        await ffmpeg.run(
            '-i', 'input.mp4',
            '-an',  // No audio
            '-c:v', 'copy',  // Copy video codec
            outputName
        );

        const data = ffmpeg.FS('readFile', outputName);

        // Clean up files
        ffmpeg.FS('unlink', 'input.mp4');
        ffmpeg.FS('unlink', outputName);

        return new Blob([data.buffer], { type: 'video/mp4' });
    } catch (error) {
        console.error("Error muting video:", error);
        throw error;
    }
}

// Trim audio file (mp3/wav) using ffmpeg
export async function trimAudio(inputFile, start, duration, outputName = 'trimmed.mp3') {
    try {
        // First check if we received a valid inputFile
        if (!inputFile || !(inputFile instanceof Blob)) {
            console.error("Invalid input file:", inputFile);
            throw new Error("Invalid audio file provided");
        }

        // Load FFmpeg
        await loadFFmpeg();

        try {
            // Try to fetch the file data
            const fileData = await fetchFile(inputFile);
            ffmpeg.FS('writeFile', 'input.mp3', fileData);
        } catch (fetchError) {
            console.error("Error fetching audio file:", fetchError);
            throw new Error(`Could not process audio file: ${fetchError.message}`);
        }

        await ffmpeg.run(
            '-ss', String(start),
            '-i', 'input.mp3',
            '-t', String(duration),
            '-acodec', 'copy',
            outputName
        );

        const data = ffmpeg.FS('readFile', outputName);

        ffmpeg.FS('unlink', 'input.mp3');
        ffmpeg.FS('unlink', outputName);

        return new Blob([data.buffer], { type: 'audio/mp3' });
    } catch (error) {
        console.error('Error trimming audio:', error);
        throw new Error(`Failed to trim audio: ${error.message || 'Unknown error'}`);
    }
}

// Mix multiple audio tracks with start times and volumes
export async function mixAudioTracks(tracks, duration, outputName = 'mixed.mp3') {
    // tracks: [{ blob, start, volume }], duration: total duration in seconds
    await loadFFmpeg();
    // Write all tracks to FS
    for (let i = 0; i < tracks.length; i++) {
        ffmpeg.FS('writeFile', `audio${i}.mp3`, await fetchFile(tracks[i].blob));
    }
    // Build filter_complex for mixing with start times and volumes
    let filter = '';
    let inputs = '';
    let amixInputs = '';
    for (let i = 0; i < tracks.length; i++) {
        // Delay in ms
        const delay = Math.round((tracks[i].start || 0) * 1000);
        // Volume
        const vol = tracks[i].volume != null ? tracks[i].volume : 1;
        filter += `[${i}:a]adelay=${delay}|${delay},volume=${vol}[a${i}];`;
        amixInputs += `[a${i}]`;
        inputs += `-i audio${i}.mp3 `;
    }
    filter += `${amixInputs}amix=inputs=${tracks.length}:duration=longest:dropout_transition=0[mixout]`;
    // Build ffmpeg args
    const args = [
        ...Array.from({ length: tracks.length }, (_, i) => ['-i', `audio${i}.mp3`]).flat(),
        '-filter_complex', filter,
        '-map', '[mixout]',
        '-t', String(duration),
        '-ac', '2',
        '-c:a', 'libmp3lame',
        outputName
    ];
    await ffmpeg.run(...args);
    const data = ffmpeg.FS('readFile', outputName);
    // Clean up
    for (let i = 0; i < tracks.length; i++) {
        ffmpeg.FS('unlink', `audio${i}.mp3`);
    }
    ffmpeg.FS('unlink', outputName);
    return new Blob([data.buffer], { type: 'audio/mp3' });
}
